import { Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Invoice from '@spike/invoice-model';
import { createInvoiceThunk, getInvoiceThunk, InvoicesStatus, cleanInvoice } from '@spike/invoices-action';
import { checkOrderThunk, MembershipsStatus } from '@spike/memberships-action';
import { Order } from '@spike/order-model';
import {
    getOrderThunk,
    migrateBookingThunk,
    OrdersStatus,
    reloadOrderThunk,
    saveOrderItemThunk,
    verifyOutdatedThunk
} from '@spike/orders-action';
import {
    confirmAndSendThunk,
    createPaymentLinkThunk,
    getEnrollmentStatusThunk,
    PaymentsStatus,
    sendThunk
} from '@spike/payments-action';
import { Payment, PaymentLink, PaymentMethodIds } from '@spike/payments-model';
import { isEmailValid } from '@spike/validations';
import { fetchWalletThunk, WalletsStatus } from '@spike/wallets-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import InvoiceDetail from 'components/Payments/Transactions/Invoices/InvoiceDetail';
import { SidePanel, Spinner } from 'components/UI';
import Receipt from 'components/UI/Receipt/Receipt';
import TwoWaySMSDrawer from 'components/UI/TwoWaySMSDrawer/TwoWaySMSDrawer';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import isEmpty from 'lodash/isEmpty';
import replace from 'lodash/replace';
import moment from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PayByLinkPath } from 'routes/Paths';
import { RootState } from 'store';
import {
    buildLateCheckoutFeeOrderItem,
    calculateAutomaticLateCheckoutFee,
    findLateCheckoutFeeCustomItem
} from 'utils/lateCheckoutFee';
import CheckoutHeader from './CheckoutHeader';
import CheckoutOrder from './CheckoutOrder';
import CheckoutPayment, { PaymentInfo } from './CheckoutPayment';
import CreditCardPayment from './CreditCardPayment2';
import DiscountModal from './DiscountModal';
import CheckoutPaymentFooter from './CheckoutPaymentFooter';
import Memberships from './Memberships';
import MembershipsButtons from './Memberships/MembershipsButtons';
import NoPhoneAlert from './NoPhoneAlert';
import PayByLink from './PayByLink';
import PaymentDone from './PaymentDone';
import PosPayment from './PosPayment';
import Transactions from './SplitPayments/Transactions';
import RefreshModal from './UI/RefreshModal';
import { getAllOrderItems } from './utils/OrderUtils';
import { getCheckoutTypeFromOrder } from './utils/getCheckoutTypeFromOrder';
import { Events } from 'analytics/events';
import Analytics from 'analytics/client';
import PromocodesSelector from './Promocodes/PromocodesSelector';
import { showSuccess } from '@spike/notifications-action';
import { status as AppoitnmentStatuses } from '@spike/appointment-model';

/* This component has the add general discount implemented, but it is cooment out because backend will implement it in a upcoming release. */

export type CheckoutType = 'singlesale' | 'membership' | 'booking';

export type TabType = 'details' | 'transactions';

export interface CheckoutProps {
    type?: CheckoutType;
    orderId?: number;
    bookingId?: number;
    paymentMethods?: Array<PaymentMethodIds>;
    hideTips?: boolean;
    hide?: boolean;
    zIndex?: number;
    onClose: () => void;
    onViewBooking?: () => void;
    onBack?: () => void;
    onEditClient?: (clientId: number) => void;
    onPaid?: (paidAmount: number) => void;
    onPayByLinkSent?: () => void;
    onPayByLinkCreated?: () => void;
    onStartPayment?: () => void;
}
const enum CheckoutSteps {
    ORDER,
    ADD_MEMBERSHIPS,
    PAYMENT,
    CREDIT_CARD,
    POS,
    PAY_BY_LINK,
    DONE,
    PROMOCODES
}

const showByStep = new Map([
    [
        CheckoutSteps.ORDER,
        {
            header: true,
            order: true,
            addMemberships: false,
            payment: false,
            invoiceResume: true,
            footer: true,
            creditCard: false,
            pos: false,
            done: false,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.ADD_MEMBERSHIPS,
        {
            header: true,
            order: false,
            addMemberships: true,
            payment: false,
            invoiceResume: false,
            footer: false,
            creditCard: false,
            pos: false,
            done: false,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.PAYMENT,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: true,
            invoiceResume: true,
            footer: true,
            creditCard: false,
            pos: false,
            done: false,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.CREDIT_CARD,
        {
            header: false,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            creditCard: true,
            pos: false,
            done: false,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.POS,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: true,
            footer: false,
            creditCard: false,
            pos: true,
            done: false,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.PAY_BY_LINK,
        {
            header: false,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            creditCard: false,
            pos: false,
            done: false,
            payByLink: true,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.DONE,
        {
            header: false,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            creditCard: false,
            pos: false,
            done: true,
            payByLink: false,
            promocodes: false
        }
    ],
    [
        CheckoutSteps.PROMOCODES,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            creditCard: false,
            pos: false,
            done: false,
            payByLink: false,
            promocodes: true
        }
    ]
]);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        },
        containerReceipt: {
            justifyContent: 'center'
        },
        horizontalPaddings: {
            paddingLeft: 32,
            paddingRight: 32,
            [theme.breakpoints.down('sm')]: {
                paddingLeft: 16,
                paddingRight: 16
            }
        },
        headerContainer: {
            paddingTop: 37,
            paddingBottom: 18,
            [theme.breakpoints.down('sm')]: {
                paddingTop: 24,
                paddingBottom: 12
            }
        },
        bodyContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            paddingBottom: 37,
            [theme.breakpoints.down('sm')]: {
                paddingBottom: 24
            }
        },
        bodyBottomSpace: {
            height: 1,
            border: '1px solid white',
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        orderSpinnerContainer: {
            display: 'flex',
            flex: 1,
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: '30%'
        },
        section: {
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        alert: {
            marginBottom: 37,
            marginLeft: 32,
            marginRight: 32,
            [theme.breakpoints.down('sm')]: {
                marginBottom: 24,
                marginLeft: 16,
                marginRight: 16
            }
        },
        membershipButtons: {
            marginTop: 24
        },
        refreshModalMembership: {
            'zIndex': 999999,
            '&': {
                zIndex: '999999 !important'
            }
        },
        tabSpacing: {
            marginBottom: 20
        }
    })
);

const getStepTitle = (step: CheckoutSteps, isBooking: boolean, tabSelected: string) => {
    switch (step) {
        case CheckoutSteps.ORDER:
            return 'Checkout';
        case CheckoutSteps.ADD_MEMBERSHIPS:
            return 'Add Membership';
        case CheckoutSteps.PAYMENT:
            return tabSelected === 'transactions' ? 'Transactions' : 'Payment Method';
        case CheckoutSteps.CREDIT_CARD:
            return isBooking ? 'Select Credit Card' : 'Credit Card';
        case CheckoutSteps.POS:
            return 'POS';
        case CheckoutSteps.PROMOCODES:
            return 'Available Promos';
        default:
            return '';
    }
};

const validate = (paymentInfo: PaymentInfo): Array<string> => {
    const errors: Array<string> = [];

    if (paymentInfo.sendInvoice && isEmpty((paymentInfo.email || '').trim())) {
        errors.push('Email is required');
    }
    if (
        paymentInfo.sendInvoice &&
        !isEmpty((paymentInfo.email || '').trim()) &&
        !isEmailValid(paymentInfo.email || '')
    ) {
        errors.push('Email is invalid.');
    }

    return errors;
};

export const Checkout: FunctionComponent<CheckoutProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();

    const order = useSelector<RootState, Order | undefined>(state => state.orders.order);
    const outdated = useSelector<
        RootState,
        { order: boolean; invoice?: boolean; invoiceFullPaid?: boolean } | undefined
    >(state => state.orders.outdated);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);

    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const paymentsEnabled = useSelector<RootState, boolean | undefined>(
        state => state.payments.enrollmentStatus?.enabled
    );
    const createdPaymentLink = useSelector<RootState, PaymentLink | null>(state => state.payments.paymentLink);
    const marketplaceId = useSelector<RootState, number | undefined>(
        state => state.login.auth.marketplaceId || state.marketplace.marketplace?.id
    );
    const marketplaceName = useSelector<RootState, string | undefined>(state => state.marketplace.marketplace?.name);

    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const orderMemberships = useSelector<RootState, { orderId: number; hasMemberships: boolean } | null>(
        state => state.memberships.orderMemberships
    );

    const walletBalance = useSelector<RootState, number>(state => state.wallets.wallet?.balance ?? 0);
    const walletsStatus = useSelector<RootState, WalletsStatus>(state => state.wallets.status);

    const checkoutType = useMemo(() => (order ? getCheckoutTypeFromOrder(order) : props.type), [order, props.type]);

    const isSingleSale = checkoutType === 'singlesale';
    const isBooking = checkoutType === 'booking';
    const isMembership = checkoutType === 'membership';
    const allowPartialPayments = isBooking || isSingleSale;
    const checkoutPanelZIndex = isMembership ? 99999 : props.zIndex;

    // A $0 order that still has line items (e.g. a free/100%-discounted package or
    // single sale) is a legit sale to close out. Packages sold as $0 arrive in the
    // `customs` bucket, so they resolve to 'singlesale' rather than 'membership';
    // this flag lets us complete them the same way $0 memberships already are.
    const orderHasItems =
        (order?.items.appointments.length ?? 0) +
            (order?.items.customs.length ?? 0) +
            (order?.items.products.length ?? 0) +
            (order?.items.addOnServices.length ?? 0) +
            (order?.items.memberships.length ?? 0) >
        0;
    const canCompleteZeroBalance = Number(invoice?.balance ?? 0) === 0 && orderHasItems;

    const getSingleSaleIssuedAt = (order?: Order) => {
        if (!isSingleSale || !order?.issuedAt || !order.createdAt) {
            return undefined;
        }

        const issuedAt = order.issuedAt.clone().tz(timeZone);
        const createdAt = order.createdAt.clone().tz(timeZone);

        if (issuedAt.isSame(createdAt, 'day')) {
            return undefined;
        }

        return issuedAt.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    };

    const [tipDistributionValid, setTipDistributionValid] = useState(true);
    const [hasMembershipsToApply, setHasMembershipsToApply] = useState(false);
    const hasMembershipsApplied = useMemo(
        () => order !== undefined && getAllOrderItems(order).some(orderItem => orderItem.redeem !== undefined),
        [order]
    );

    const initialStepSet = useRef(false);
    const pendingLateFeeInvoiceRef = useRef(false);

    const createCheckoutInvoice = (currentOrder: Order) => {
        dispatch(
            createInvoiceThunk(apiClientWrapper, currentOrder.id, props.bookingId, getSingleSaleIssuedAt(currentOrder))
        );
    };

    const ensureLateCheckoutFeeBeforeInvoice = (currentOrder?: Order) => {
        if (!currentOrder) return;

        const automaticAmount = calculateAutomaticLateCheckoutFee(currentOrder.items.appointments ?? [], timeZone);
        const existingFee = findLateCheckoutFeeCustomItem(currentOrder);
        if (!currentOrder.paid && automaticAmount > 0 && !existingFee) {
            pendingLateFeeInvoiceRef.current = true;
            dispatch(
                saveOrderItemThunk(
                    apiClientWrapper,
                    buildLateCheckoutFeeOrderItem({
                        order: currentOrder,
                        amount: automaticAmount,
                        timeZone,
                        uuid: apiClientWrapper.uuid()
                    })
                )
            );
            return;
        }

        pendingLateFeeInvoiceRef.current = false;
        createCheckoutInvoice(currentOrder);
    };

    const [show, setShow] = useState(showByStep.get(CheckoutSteps.PAYMENT)!);

    const [tabSelected, setTabSelected] = useState<TabType>('details');

    const [loading, setLoading] = useState({
        order: true,
        invoice: true,
        enrollmentStatus: true,
        memberships: true,
        wallet: false,
        tip: false,
        payment: false,
        outdated: false,
        removingPromocode: false
    });

    const [showModal, setShowModal] = useState({ discount: false, refresh: false });
    const [checkoutStep, setCheckoutStep] = useState(CheckoutSteps.PAYMENT);
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | undefined>();
    const [amountToPay, setAmountToPay] = useState<number>(0);
    const [paidAmount, setPaidAmount] = useState<number>(0);
    const creditCardChargedAmountRef = useRef<number | null>(null);
    const [errors, setErrors] = useState<Array<string>>([]);
    const [paymentMethodNotSelected, setPaymentMethodNotSelected] = useState(false);
    const [initiatedTracked, setInitiatedTracked] = useState(false);
    const [amountToPayMessageError, setAmountToPayMessageError] = useState<string | undefined>();
    const [buttonLabel, setButtonLabel] = useState('Pay');
    const [payLink, setPayLink] = useState('');
    const [showReceipt, setShowReceipt] = useState<Payment | undefined>(undefined);
    const [showTwoWays, setShowTwoWays] = useState<boolean>(false);
    const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

    const showClientWithoutPhoneAlert =
        paymentInfo?.paymentMethodId === PaymentMethodIds.PayByLink && isEmpty(invoice?.customer?.phone);

    const buttonDisabled =
        loading.order ||
        loading.invoice ||
        loading.enrollmentStatus ||
        loading.wallet ||
        loading.tip ||
        loading.outdated ||
        loading.removingPromocode ||
        showClientWithoutPhoneAlert ||
        (checkoutStep === CheckoutSteps.PAYMENT && !tipDistributionValid) ||
        (checkoutStep === CheckoutSteps.PAYMENT &&
            (paymentInfo?.walletApplied ?? false) &&
            (walletBalance === 0 || amountToPay > walletBalance));

    const showButton =
        checkoutStep === CheckoutSteps.ORDER ||
        (!loading.order &&
            !loading.invoice &&
            checkoutStep == CheckoutSteps.PAYMENT &&
            (Number(invoice?.balance ?? '0') > 0 || isMembership || canCompleteZeroBalance));

    const disableTips = useMemo(() => {
        return (
            order &&
            invoice &&
            order.items.appointments.every(appItem =>
                [
                    AppoitnmentStatuses.COMPLETED,
                    AppoitnmentStatuses.CANCELLED,
                    AppoitnmentStatuses.NO_SHOW,
                    AppoitnmentStatuses.DECLINED
                ].includes(appItem.appointment.status.id)
            ) &&
            Number(invoice.balance) === 0 &&
            !isSingleSale
        );
    }, [order, invoice]);

    useEffect(() => {
        if (!checkoutType || initialStepSet.current) return;
        if (isBooking && loading.memberships) return;

        const step = isBooking
            ? hasMembershipsToApply || hasMembershipsApplied
                ? CheckoutSteps.ORDER
                : CheckoutSteps.PAYMENT
            : CheckoutSteps.PAYMENT;

        setCheckoutStep(step);
        setShow(showByStep.get(step)!);
        initialStepSet.current = true;
    }, [checkoutType, isBooking, hasMembershipsToApply, hasMembershipsApplied, loading.memberships]);

    useEffect(() => {
        dispatch(cleanInvoice());
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));

        if (props.orderId) {
            dispatch(getOrderThunk(apiClientWrapper, props.orderId));
            dispatch(checkOrderThunk(apiClientWrapper, props.orderId));
        } else if (props.bookingId) {
            props.bookingId && dispatch(migrateBookingThunk(apiClientWrapper, props.bookingId));
        } else {
            props.onClose();
        }
    }, []);

    useEffect(() => {
        if (invoice && Number(invoice.balance) <= 0 && checkoutStep === CheckoutSteps.PAYMENT) {
            setButtonLabel('Complete');
        } else if (paymentInfo?.paymentMethodId === PaymentMethodIds.PayByLink) {
            setButtonLabel('Send Link');
        } else if (checkoutStep === CheckoutSteps.ORDER) {
            setButtonLabel('Next');
        } else {
            setButtonLabel('Pay');
        }
    }, [invoice, paymentInfo, checkoutStep]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.GetSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                ensureLateCheckoutFeeBeforeInvoice(order);
                break;
            case OrdersStatus.ReloadSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.MigrateSuccess:
                ensureLateCheckoutFeeBeforeInvoice(order);
                order && dispatch(checkOrderThunk(apiClientWrapper, order.id));
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.SaveOrderItemSuccess:
                if (pendingLateFeeInvoiceRef.current && order) {
                    pendingLateFeeInvoiceRef.current = false;
                    createCheckoutInvoice(order);
                }
                break;
            case OrdersStatus.VerifyOutdatedSuccess:
                setLoading(prev => ({ ...prev, outdated: false }));
                if (outdated?.order || outdated?.invoice) {
                    setShowModal(prev => ({ ...prev, refresh: true }));
                } else {
                    payHandler();
                }
                break;
            case OrdersStatus.Error:
                setLoading(prev => ({ ...prev, order: false, outdated: false }));
                if (pendingLateFeeInvoiceRef.current && order) {
                    pendingLateFeeInvoiceRef.current = false;
                    createCheckoutInvoice(order);
                }
                break;
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.GetInvoiceSuccess:
            case InvoicesStatus.CreateInvoiceSuccess:
                setAmountToPay(Number(invoice?.balance));
                setLoading(prev => ({ ...prev, invoice: false }));
                if ((invoice?.configuredDiscounts.discounts.length ?? 0) > 0) {
                    dispatch(showSuccess('Automatic Discounts applied successfully.'));
                }
                if (invoice?.customer?.id) {
                    setLoading(prev => ({ ...prev, wallet: true }));
                    dispatch(fetchWalletThunk(apiClientWrapper, invoice.customer.id));
                }
                // Fire Client Initiated Checkout once when invoice becomes available
                if (!initiatedTracked && marketplaceId) {
                    try {
                        const invId = invoice?.id ?? undefined;
                        const guardKey = invId ? `evt_once_CLIENT_INITIATED_CHECKOUT_${invId}` : undefined;
                        const alreadySent = guardKey ? sessionStorage.getItem(guardKey) === '1' : false;
                        if (!alreadySent) {
                            Analytics.track(Events.CLIENT_INITIATED_CHECKOUT as any, {
                                marketplace_id: marketplaceId,
                                checkout_type: checkoutType,
                                order_id: order?.id,
                                booking_id: props.bookingId,
                                invoice_id: invoice?.id,
                                invoice_balance: Number(invoice?.balance ?? 0)
                            });
                            guardKey && sessionStorage.setItem(guardKey, '1');
                        }
                    } catch (e) {
                        // non-blocking
                    }
                    setInitiatedTracked(true);
                }
                break;
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, invoice: false }));
                break;
        }
    }, [invoicesStatus]);

    useNonInitialEffect(() => {
        switch (walletsStatus) {
            case WalletsStatus.FetchSuccess:
            case WalletsStatus.Error:
                setLoading(prev => ({ ...prev, wallet: false }));
                break;
        }
    }, [walletsStatus]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.CheckOrderSuccess:
                const hasMemberships =
                    orderMemberships !== null &&
                    (orderMemberships.orderId === props.orderId || orderMemberships.orderId === order?.id) &&
                    orderMemberships.hasMemberships;
                setHasMembershipsToApply(hasMemberships);
                setLoading(prev => ({ ...prev, memberships: false }));
                break;
            case MembershipsStatus.CheckOrderError:
                setHasMembershipsToApply(false);
                setLoading(prev => ({ ...prev, memberships: false }));
                break;
        }
    }, [membershipsStatus]);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GetEnrollmentStatusSuccess:
                setLoading(prev => ({ ...prev, enrollmentStatus: false }));
                break;
            case PaymentsStatus.SendSuccess:
                const totalPaid = creditCardChargedAmountRef.current ?? amountToPay;
                creditCardChargedAmountRef.current = null;
                if (!showReceipt) {
                    setPaidAmount(totalPaid);
                    setCheckoutStep(CheckoutSteps.DONE);
                    setShow(showByStep.get(CheckoutSteps.DONE)!);
                    setLoading(prev => ({ ...prev, payment: false }));
                }
                invoice && props.onPaid?.(totalPaid);
                break;
            case PaymentsStatus.CreatePaymentLinkSuccess:
                if (createdPaymentLink) {
                    const payLinkUrlPath = replace(PayByLinkPath, ':uuid', createdPaymentLink.uuid);
                    const fullPayLinkUrl = `${process.env.REACT_APP_BASE_URL}${payLinkUrlPath}`;
                    setPayLink(fullPayLinkUrl);

                    setCheckoutStep(CheckoutSteps.PAY_BY_LINK);
                    setShow(showByStep.get(CheckoutSteps.PAY_BY_LINK)!);
                    setLoading(prev => ({ ...prev, payment: false }));
                    props.onPayByLinkCreated && props.onPayByLinkCreated();
                }
                break;
            case PaymentsStatus.Error:
                creditCardChargedAmountRef.current = null;
                setLoading(prev => ({ ...prev, enrollmentStatus: false, payment: false }));
        }
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        if (!props.hide) {
            setLoading(prev => ({ ...prev, order: true, invoice: true }));
            dispatch(
                createInvoiceThunk(apiClientWrapper, order?.id ?? 0, props.bookingId, getSingleSaleIssuedAt(order))
            );
            dispatch(reloadOrderThunk(apiClientWrapper));
        }
    }, [props.hide]);

    useNonInitialEffect(() => {
        setAmountToPay(Number(invoice?.balance ?? 0));
    }, [invoice?.balance]);

    /*const addDiscountHandler = () => {
        setShowModal(prev => ({...prev, discount: true}));
    };*/

    const payRemainingBalanceHandler = () => {
        setPaymentInfo(undefined);
        setLoading(prev => ({ ...prev, order: true, invoice: true }));
        setCheckoutStep(CheckoutSteps.PAYMENT);
        setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
        if (order?.id) {
            dispatch(reloadOrderThunk(apiClientWrapper));
            dispatch(createInvoiceThunk(apiClientWrapper, order.id, props.bookingId));
        }
    };

    const clickPayButtonHandler = () => {
        setAmountToPayMessageError(undefined);
        setPaymentMethodNotSelected(false);
        if (
            checkoutStep === CheckoutSteps.PAYMENT &&
            Number(invoice?.balance ?? 0) === 0 &&
            (isMembership || orderHasItems)
        ) {
            setLoading(prev => ({ ...prev, payment: true }));
            invoice &&
                dispatch(sendThunk(apiClientWrapper, invoice.id!, false, false, false, paymentInfo?.email ?? ''));
        } else if (checkoutStep === CheckoutSteps.PAYMENT && amountToPay <= 0) {
            setAmountToPayMessageError('Amount must be greater than 0');
        } else if (
            checkoutStep === CheckoutSteps.PAYMENT &&
            ![PaymentMethodIds.Cash.toString(), PaymentMethodIds.Other.toString()].includes(
                paymentInfo?.paymentMethodId ?? ''
            ) &&
            amountToPay > Number(invoice?.balance ?? 0)
        ) {
            setAmountToPayMessageError('The amount must not exceed the balance due');
        } else if (checkoutStep === CheckoutSteps.PAYMENT && !paymentInfo?.paymentMethodId) {
            setPaymentMethodNotSelected(true);
        } else {
            // Track Pay click after validations pass
            if (marketplaceId) {
                try {
                    Analytics.track(Events.CLIENT_PAY_CLICKED as any, {
                        marketplace_id: marketplaceId,
                        checkout_type: checkoutType,
                        order_id: order?.id,
                        booking_id: props.bookingId,
                        invoice_id: invoice?.id,
                        invoice_balance: Number(invoice?.balance ?? 0),
                        amount_to_pay: amountToPay,
                        payment_method_id: paymentInfo?.paymentMethodId ?? null,
                        send_invoice: paymentInfo?.sendInvoice ?? false,
                        email_present: !!(paymentInfo?.email && paymentInfo.email.trim().length > 0),
                        tip_amount: Number(paymentInfo?.tip?.amount ?? 0),
                        pos_terminal_id: paymentInfo?.posTerminalId ?? null,
                        pos_service_id: paymentInfo?.posServiceId ?? null
                    });
                } catch (e) {
                    // non-blocking
                }
            }
            setLoading(prev => ({ ...prev, outdated: true }));
            order?.id &&
                invoice?.id &&
                dispatch(verifyOutdatedThunk(apiClientWrapper, order.id, order.version, invoice.id, invoice.version));
        }
    };

    const payHandler = () => {
        if (checkoutStep === CheckoutSteps.ORDER) {
            setCheckoutStep(CheckoutSteps.PAYMENT);
            setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
        } else if (checkoutStep === CheckoutSteps.PAYMENT) {
            const errors = validate(paymentInfo!);
            setErrors(errors);
            if (errors.length === 0) {
                props.onStartPayment && props.onStartPayment();
                if (Number(invoice?.balance) > 0) {
                    switch (paymentInfo?.paymentMethodId) {
                        case PaymentMethodIds.Cash:
                        case PaymentMethodIds.Other:
                            setLoading(prev => ({ ...prev, payment: true }));
                            invoice?.id &&
                                paymentInfo.paymentMethodId &&
                                dispatch(
                                    confirmAndSendThunk(
                                        apiClientWrapper,
                                        invoice.id,
                                        paymentInfo.paymentMethodId,
                                        paymentInfo.sendInvoice,
                                        false,
                                        false,
                                        paymentInfo.email ?? '',
                                        undefined,
                                        allowPartialPayments ? amountToPay : Number(invoice?.balance ?? 0)
                                    )
                                );
                            break;
                        case PaymentMethodIds.CreditCard:
                            setCheckoutStep(CheckoutSteps.CREDIT_CARD);
                            setShow(showByStep.get(CheckoutSteps.CREDIT_CARD)!);
                            break;
                        case PaymentMethodIds.POS:
                            setCheckoutStep(CheckoutSteps.POS);
                            setShow(showByStep.get(CheckoutSteps.POS)!);
                            break;
                        case PaymentMethodIds.PayByLink:
                            setLoading(prev => ({ ...prev, payment: true }));
                            invoice?.id &&
                                dispatch(
                                    createPaymentLinkThunk(
                                        apiClientWrapper,
                                        invoice.id,
                                        allowPartialPayments ? amountToPay : undefined
                                    )
                                );
                            break;
                        case PaymentMethodIds.Wallet: {
                            setLoading(prev => ({ ...prev, payment: true }));
                            const walletAmt = allowPartialPayments ? amountToPay : Number(invoice?.balance ?? 0);
                            invoice?.id &&
                                dispatch(
                                    confirmAndSendThunk(
                                        apiClientWrapper,
                                        invoice.id,
                                        PaymentMethodIds.Wallet,
                                        paymentInfo.sendInvoice,
                                        false,
                                        false,
                                        paymentInfo.email ?? '',
                                        undefined,
                                        walletAmt
                                    )
                                );
                            break;
                        }
                    }
                } else {
                    setLoading(prev => ({ ...prev, payment: true }));
                    dispatch(
                        invoice?.id &&
                            confirmAndSendThunk(
                                apiClientWrapper,
                                invoice.id,
                                PaymentMethodIds.Other,
                                paymentInfo?.sendInvoice ?? false,
                                false,
                                false,
                                paymentInfo?.email ?? '',
                                undefined,
                                allowPartialPayments ? amountToPay : undefined
                            )
                    );
                }
            }
        }
    };

    const backHandler = () => {
        switch (checkoutStep) {
            case CheckoutSteps.ORDER:
                props.onBack && props.onBack();
                break;
            case CheckoutSteps.ADD_MEMBERSHIPS:
                setCheckoutStep(CheckoutSteps.ORDER);
                setShow(showByStep.get(CheckoutSteps.ORDER)!);
                break;
            case CheckoutSteps.PAYMENT:
                if (isBooking && !showReceipt && (hasMembershipsToApply || hasMembershipsApplied)) {
                    setCheckoutStep(CheckoutSteps.ORDER);
                    setShow(showByStep.get(CheckoutSteps.ORDER)!);
                    setPaymentInfo(undefined);
                    setTabSelected('details');
                } else if (showReceipt) {
                    setShowReceipt(undefined);
                } else if (props.onBack) {
                    props.onBack();
                }
                break;
            case CheckoutSteps.CREDIT_CARD:
            case CheckoutSteps.POS:
                setCheckoutStep(CheckoutSteps.PAYMENT);
                setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
                break;
            case CheckoutSteps.PROMOCODES:
                setCheckoutStep(CheckoutSteps.PAYMENT);
                setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
                break;
        }
    };

    const applyMembershipsHandler = () => {
        setCheckoutStep(CheckoutSteps.ADD_MEMBERSHIPS);
        setShow(showByStep.get(CheckoutSteps.ADD_MEMBERSHIPS)!);
    };

    const changePaymentInfoHandler = (paymentInfo: PaymentInfo) => {
        setPaymentMethodNotSelected(false);
        setPaymentInfo(paymentInfo);
    };

    const successCreditCardPaymentHandler = (chargedAmount: number) => {
        creditCardChargedAmountRef.current = chargedAmount;
        setPaidAmount(chargedAmount);
        setLoading(prev => ({ ...prev, payment: true }));
        invoice &&
            paymentInfo?.paymentMethodId === PaymentMethodIds.CreditCard &&
            dispatch(
                confirmAndSendThunk(
                    apiClientWrapper,
                    invoice.id!,
                    paymentInfo.paymentMethodId,
                    paymentInfo.sendInvoice,
                    false,
                    false,
                    paymentInfo.email ?? '',
                    undefined,
                    allowPartialPayments ? amountToPay : undefined
                )
            );
    };

    const successPosPaymentHandler = () => {
        setLoading(prev => ({ ...prev, payment: true }));
        invoice &&
            paymentInfo?.paymentMethodId === PaymentMethodIds.POS &&
            dispatch(
                sendThunk(apiClientWrapper, invoice.id!, paymentInfo.sendInvoice, false, false, paymentInfo.email ?? '')
            );
    };

    const addPhoneNumberHandler = () => {
        props.onEditClient && invoice?.customer?.id && props.onEditClient(invoice?.customer?.id);
    };

    const refreshHandler = () => {
        if (props.onBack) {
            props.onBack();
        } else {
            props.onClose();
        }
    };

    const refundedHanlder = () => {
        invoice?.id && dispatch(getInvoiceThunk(apiClientWrapper, invoice?.id));
        setShowReceipt(undefined);
    };

    const showPromocodeSelectorHandler = () => {
        setCheckoutStep(CheckoutSteps.PROMOCODES);
        setShow(showByStep.get(CheckoutSteps.PROMOCODES)!);
    };

    const backPromocodeSelectorHandler = () => {
        setCheckoutStep(CheckoutSteps.PAYMENT);
        setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
    };

    const viewBookingHandler = useCallback(() => {
        const orderId = props.orderId ?? order?.id;
        if (orderId) {
            dispatch(getOrderThunk(apiClientWrapper, orderId));
        } else {
            dispatch(reloadOrderThunk(apiClientWrapper));
        }

        const invoiceId = invoice?.id ?? order?.invoiceId;
        if (invoiceId) {
            dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
        }

        if (props.onViewBooking) {
            props.onViewBooking();
        } else {
            props.onClose();
        }
    }, [
        apiClientWrapper,
        dispatch,
        invoice?.id,
        order?.id,
        order?.invoiceId,
        props.onClose,
        props.onViewBooking,
        props.orderId
    ]);

    const canViewZeroInvoice =
        Boolean(invoice?.id) &&
        Boolean(marketplaceId) &&
        paidAmount === 0 &&
        Number(invoice?.grandTotal ?? 0) === 0 &&
        Number(invoice?.payments?.total ?? 0) === 0;

    return (
        <>
            <SidePanel
                side="right"
                showCloseButton={true}
                onClose={props.onClose}
                zIndex={checkoutPanelZIndex}
                hide={props.hide || showInvoiceDetail}
            >
                <Box className={clsx(classes.container, { [classes.containerReceipt]: showReceipt })}>
                    {loading.order ? (
                        <Box className={classes.orderSpinnerContainer}>
                            <Spinner />
                        </Box>
                    ) : (
                        <>
                            {show.done && (
                                <PaymentDone
                                    date={moment()}
                                    total={paidAmount.toFixed(2)}
                                    remainingBalance={Math.max(0, Number(invoice?.balance ?? 0) - amountToPay)}
                                    type={checkoutType ?? 'singlesale'}
                                    onClose={props.onClose}
                                    onViewBooking={viewBookingHandler}
                                    onViewInvoice={canViewZeroInvoice ? () => setShowInvoiceDetail(true) : undefined}
                                    onPayRemainingBalance={payRemainingBalanceHandler}
                                />
                            )}
                            {show.header && !showReceipt && (
                                <Box className={clsx(classes.headerContainer, classes.horizontalPaddings)}>
                                    <CheckoutHeader
                                        title={getStepTitle(checkoutStep, isBooking, tabSelected)}
                                        loading={loading.order || loading.invoice || loading.enrollmentStatus}
                                        tabSelected={tabSelected}
                                        onBack={
                                            props.onBack || (checkoutStep !== CheckoutSteps.ORDER && isBooking)
                                                ? backHandler
                                                : undefined
                                        }
                                        onClose={props.onClose}
                                        onSelectTab={show.payment && isBooking ? setTabSelected : undefined}
                                        onAddDiscount={/*addDiscountHandler*/ undefined}
                                    />
                                </Box>
                            )}
                            {showReceipt && (
                                <Receipt
                                    payment={showReceipt}
                                    withClose={true}
                                    onBack={() => setShowReceipt(undefined)}
                                    onRefunded={refundedHanlder}
                                />
                            )}
                            {!show.addMemberships && !showReceipt && !show.creditCard && !show.promocodes && (
                                <OverlayScrollbarsComponent style={{ height: '100%' }}>
                                    <Box className={clsx(classes.bodyContainer, classes.horizontalPaddings)}>
                                        {show.order && !loading.order && order !== undefined && (
                                            <CheckoutOrder
                                                order={order}
                                                matchMemebrships={hasMembershipsToApply || hasMembershipsApplied}
                                                onClickChat={() => setShowTwoWays(true)}
                                            />
                                        )}
                                        {show.order &&
                                            !loading.order &&
                                            order !== undefined &&
                                            (hasMembershipsToApply || hasMembershipsApplied) && (
                                                <MembershipsButtons
                                                    order={order}
                                                    className={classes.membershipButtons}
                                                    onClickButton={applyMembershipsHandler}
                                                />
                                            )}
                                        {show.payment &&
                                            tabSelected === 'details' &&
                                            !loading.invoice &&
                                            !loading.enrollmentStatus &&
                                            !loading.order &&
                                            invoice !== undefined && (
                                                <CheckoutPayment
                                                    invoice={invoice}
                                                    isBooking={isBooking}
                                                    initPaymentMethodId={paymentInfo?.paymentMethodId}
                                                    paymentsEnabled={paymentsEnabled ?? false}
                                                    paymentMethods={props.paymentMethods}
                                                    hideTips={props.hideTips}
                                                    disableTips={disableTips}
                                                    hideSendingInformation={
                                                        isMembership && Number(invoice?.balance ?? 0) === 0
                                                    }
                                                    disabled={loading.payment || loading.removingPromocode}
                                                    paymentMethodError={paymentMethodNotSelected}
                                                    walletBalance={walletBalance}
                                                    onSavingTip={saving =>
                                                        setLoading(prev => ({ ...prev, tip: saving }))
                                                    }
                                                    onChangePaymentInfo={changePaymentInfoHandler}
                                                    onTipDistributionValidChange={setTipDistributionValid}
                                                />
                                            )}

                                        {order &&
                                            show.payment &&
                                            tabSelected === 'transactions' &&
                                            !showReceipt &&
                                            !loading.invoice &&
                                            !loading.enrollmentStatus &&
                                            !loading.order &&
                                            invoice?.id !== undefined &&
                                            invoice?.id !== null && (
                                                <Transactions
                                                    invoiceId={invoice.id}
                                                    onPay={() => setTabSelected('details')}
                                                    onViewPayment={(payment: Payment) => setShowReceipt(payment)}
                                                />
                                            )}

                                        {show.pos && invoice && (
                                            <PosPayment
                                                invoiceId={invoice.id!}
                                                className={classes.section}
                                                hideTitle={true}
                                                amountToPay={amountToPay}
                                                onSuccess={successPosPaymentHandler}
                                                onGetPosServiceId={(posTerminalId, posServiceId) =>
                                                    setPaymentInfo(prev => ({
                                                        ...prev!,
                                                        posTerminalId,
                                                        posServiceId
                                                    }))
                                                }
                                            />
                                        )}
                                        {show.payByLink && invoice && (
                                            <PayByLink
                                                payLink={payLink}
                                                amountToPay={amountToPay.toFixed(2)}
                                                onClose={() => props.onPayByLinkSent && props.onPayByLinkSent()}
                                            />
                                        )}
                                        {!loading.order && order && <Box className={classes.bodyBottomSpace} />}
                                    </Box>
                                </OverlayScrollbarsComponent>
                            )}
                            {showClientWithoutPhoneAlert && !loading.order && !loading.invoice && (
                                <NoPhoneAlert
                                    className={classes.alert}
                                    onAddPhoneNumber={addPhoneNumberHandler}
                                />
                            )}
                            {show.creditCard && invoice && invoice.id && invoice.customer && (
                                <CreditCardPayment
                                    invoiceUuid={invoice.uuid}
                                    invoiceId={invoice.id}
                                    onSuccess={successCreditCardPaymentHandler}
                                    onBack={backHandler}
                                    clientId={invoice.customer.id}
                                    amount={amountToPay}
                                />
                            )}
                            {show.promocodes && invoice?.id && (
                                <PromocodesSelector
                                    invoice={invoice}
                                    onAdded={backPromocodeSelectorHandler}
                                    onClose={backPromocodeSelectorHandler}
                                />
                            )}
                            {show.footer && tabSelected !== 'transactions' && !showReceipt && (
                                <CheckoutPaymentFooter
                                    invoice={invoice}
                                    isBooking={isBooking}
                                    showInvoiceResume={show.invoiceResume}
                                    showAmountToPay={
                                        show.payment &&
                                        tabSelected === 'details' &&
                                        !loading.invoice &&
                                        !loading.enrollmentStatus &&
                                        !loading.order &&
                                        invoice !== undefined &&
                                        Number(invoice?.balance) > 0 &&
                                        allowPartialPayments &&
                                        !loading.tip
                                    }
                                    showPayButton={showButton}
                                    amountToPay={amountToPay}
                                    amountToPayError={amountToPayMessageError}
                                    errors={errors}
                                    buttonLabel={buttonLabel}
                                    buttonDisabled={buttonDisabled}
                                    buttonLoading={loading.payment || loading.outdated}
                                    loadingInvoice={loading.invoice || loading.removingPromocode}
                                    loadingTip={loading.tip}
                                    onAmountToPayChange={setAmountToPay}
                                    onPay={clickPayButtonHandler}
                                    onAddPromocode={showPromocodeSelectorHandler}
                                    onStartRemovingPromocode={() =>
                                        setLoading(prev => ({ ...prev, removingPromocode: true }))
                                    }
                                    onStopRemovingPromocode={() =>
                                        setLoading(prev => ({ ...prev, removingPromocode: false }))
                                    }
                                />
                            )}
                            {show.addMemberships && order !== undefined && (
                                <Memberships
                                    order={order}
                                    bookingId={props.bookingId}
                                    onClose={backHandler}
                                />
                            )}
                        </>
                    )}
                </Box>
            </SidePanel>

            {order && showTwoWays && (
                <TwoWaySMSDrawer
                    onBack={() => setShowTwoWays(false)}
                    client={order.client}
                    onClose={props.onClose}
                    offset={590}
                    hideBackdrop={true}
                    zIndex={10100}
                />
            )}
            {showModal.discount && (
                <DiscountModal
                    total={295}
                    onClose={() => setShowModal(prev => ({ ...prev, discount: false }))}
                />
            )}
            {showModal.refresh && (
                <RefreshModal
                    refreshButtonLabel="Refresh Checkout"
                    onRefresh={refreshHandler}
                    onClose={() => setShowModal(prev => ({ ...prev, refresh: false }))}
                    className={isMembership ? classes.refreshModalMembership : undefined}
                />
            )}
            {showInvoiceDetail && invoice?.id && marketplaceId && (
                <InvoiceDetail
                    invoiceId={invoice.id}
                    marketplaceId={marketplaceId}
                    marketplaceName={marketplaceName}
                    zIndex={(checkoutPanelZIndex ?? 999) + 1}
                    isCheckoutDisabled
                    onClose={() => setShowInvoiceDetail(false)}
                />
            )}
        </>
    );
};

export default Checkout;
